import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CalendarClock, Mail } from "lucide-react";

function planEndDate(plan) {
  const weeks = Number(plan?.overview?.total_duration_weeks || 0);
  const base = plan?.created_at ? new Date(plan.created_at) : new Date();
  const d = new Date(base.getTime() + weeks * 7 * 86400000);
  try { return d.toISOString().slice(0, 10); } catch { return new Date().toISOString().slice(0, 10); }
}

export default function RecallReminderModal({ open, onOpenChange, plan, phase = null, onDone }) {
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [typeId, setTypeId] = useState("auto");
  const [types, setTypes] = useState([]);
  const [sendNow, setSendNow] = useState(true);
  const [cadence, setCadence] = useState("series");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && plan) {
      if (phase) {
        setDate(phase.startDate || planEndDate(plan));
        setReason(`Start Phase ${phase.number}: ${phase.title}`);
      } else {
        setDate(planEndDate(plan));
        setReason("Review after your plan");
      }
      setSendNow(true);
      setCadence("series");
      setTypeId("auto");
    }
    // Re-seed defaults only when the modal opens or targets a different plan/phase.
    // Intentionally keyed on ids (not the plan/phase objects) so a Builder re-render
    // while the modal is open does not wipe the user's edited date/reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan?.id, phase?.index]);

  useEffect(() => {
    if (open) api.get("/cliniko/recall-types").then((r) => setTypes(r.data.recall_types || [])).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!date) { toast.error("Pick a recall date"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/plans/${plan.id}/recall`, {
        recall_at: date, reason,
        recall_type_id: typeId === "auto" ? undefined : typeId,
        send_now: sendNow, cadence,
      });
      const parts = [];
      if (data.cliniko_recall) parts.push("Cliniko recall");
      if (data.app_recall) parts.push("dashboard recall");
      if (data.reminder_scheduled) parts.push(`${data.reminders_count || 1} reminder email${(data.reminders_count || 1) > 1 ? "s" : ""}`);
      if (data.emailed_now) parts.push("note emailed now");
      toast.success(parts.length ? `Set: ${parts.join(", ")}` : "Recall set");
      if (data.note) toast.message(data.note);
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set recall"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white" data-testid="recall-reminder-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><CalendarClock className="h-4 w-4 text-teal-600" /> Set recall &amp; reminder</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Creates a Cliniko recall + a dashboard recall, and schedules a booking-link email on the due date.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Revisit date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="recall-reminder-date" className="mt-1" />
              <p className="mt-0.5 text-[10px] text-slate-400">Defaults to the end of the plan — adjust as needed.</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Cliniko recall type</Label>
              <Select value={typeId} onValueChange={setTypeId}>
                <SelectTrigger className="mt-1" data-testid="recall-reminder-type"><SelectValue placeholder="Auto" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="auto">Auto (first available)</SelectItem>
                  {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Reason</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="recall-reminder-reason" className="mt-1" />
          </div>
          <label className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5 ring-1 ring-slate-200">
            <span className="flex items-center gap-2 text-sm text-slate-600"><Mail className="h-4 w-4 text-indigo-500" /> Email the patient a note now</span>
            <Switch checked={sendNow} onCheckedChange={setSendNow} data-testid="recall-reminder-sendnow" />
          </label>
          <div>
            <Label className="text-xs text-slate-500">Reminder emails</Label>
            <Select value={cadence} onValueChange={setCadence}>
              <SelectTrigger className="mt-1" data-testid="recall-reminder-cadence"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="series">Series — due date, +1 week, +3 weeks</SelectItem>
                <SelectItem value="single">Single — due date only</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-0.5 text-[10px] text-slate-400">Automatic reminders stop early once the patient rebooks.</p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="recall-reminder-confirm" className="bg-teal-600 hover:bg-teal-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set recall & reminder"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
